import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Search, Cpu, CheckSquare, Settings, Smartphone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const iconMap = { Search, Cpu, CheckSquare, Settings };

const TechSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  const techFeatures = t('tech.features') || [];

  return (
    <section id="services" ref={ref} className="py-16 sm:py-28 relative noise-overlay" style={{ background: 'linear-gradient(180deg, #1e40af 0%, #2563eb 100%)' }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12 sm:mb-20"
        >
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-5">
            <span className="gold-text-shimmer">{t('tech.labComesToYou')}</span>
          </h2>
          <p className="text-blue-100 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            {t('tech.labSubtitle')}
          </p>
        </motion.div>

        {/* Features */}
        <div className="relative mb-14 sm:mb-24">
          {/* Connecting line - desktop only */}
          <div className="hidden lg:block absolute top-14 left-[8%] right-[8%] h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.15), rgba(96, 165, 250, 0.15), transparent)' }} />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8">
            {techFeatures.map((feature, index) => {
              const IconComponent = iconMap[['Search', 'Cpu', 'CheckSquare', 'Settings'][index]];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                  className="relative group cursor-default"
                >
                  {/* Desktop dot */}
                  <div className="hidden lg:flex items-center justify-center mb-6">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center relative z-10"
                      style={{ background: 'linear-gradient(180deg, #1e40af 0%, #2563eb 100%)', border: '2px solid rgba(96, 165, 250, 0.25)' }}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ background: '#c9a84c' }} />
                    </div>
                  </div>

                  <div
                    className="p-4 sm:p-7 h-full text-center transition-all duration-400"
                    style={{
                      borderRadius: '1.2rem',
                      background: 'rgba(14, 30, 56, 0.4)',
                      borderTop: '2px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderTopColor = 'rgba(96, 165, 250, 0.3)';
                      e.currentTarget.style.background = 'rgba(30, 58, 138, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderTopColor = 'transparent';
                      e.currentTarget.style.background = 'rgba(14, 30, 56, 0.4)';
                    }}
                  >
                    <div className="mb-3 sm:mb-4">
                      {IconComponent && <IconComponent size={22} style={{ color: '#ffffff' }} className="mx-auto" />}
                    </div>
                    <h3 className="text-sm sm:text-lg font-bold text-white mb-2 sm:mb-3">{feature.title}</h3>
                    <p className="text-blue-100 text-xs sm:text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Brands */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-center"
        >
          <h3 className="text-lg sm:text-2xl font-bold text-white mb-6 sm:mb-10">{t('tech.title')}</h3>
          <p className="text-blue-100 text-xs sm:text-base mb-8 sm:mb-12 max-w-xl mx-auto px-2">
            {t('tech.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-5">
            {t('tech.brands').map((brand, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.7 + i * 0.06, duration: 0.4 }}
                whileHover={{ scale: 1.05 }}
                className="px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-bold text-white"
                style={{
                  background: 'rgba(30, 64, 175, 0.4)',
                  border: '1px solid rgba(96, 165, 250, 0.3)',
                }}
              >
                {brand}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24" style={{ background: 'linear-gradient(to top, #1e3a8a, transparent)' }} />
    </section>
  );
};

export default TechSection;
